import { CANVAS_ELEMENT_TYPES } from "../nativeCanvasElements";
import { getArrowPoints, getElementBounds } from "../geometry/elementBounds";
import {
  getResizeHandleCenters,
  getScreenBounds,
  SELECTION_HANDLE_SIZE,
} from "../geometry/selectionHandles";
import { sceneToScreen } from "../geometry/viewportTransform";
import { isBoxElement } from "../nativeCanvasElements";
import { drawCanvasElement } from "./drawElement";
import { applySceneTransform, clearCanvas } from "./drawScene";

const BIND_HIGHLIGHT_COLOR = "#a59cff";
const BIND_HIGHLIGHT_PADDING = 4;
const HANDLE_FILL = "#ffffff";
const MARQUEE_FILL = "rgba(255, 159, 67, 0.12)";
const SELECTION_COLOR = "#ff9f43";
const SELECTION_DASH = Object.freeze([6, 4]);
const SELECTION_PADDING = 4;
const SELECTION_STROKE_WIDTH = 1.5;

const getEffectiveElement = ({ element, liftedElements }) =>
  liftedElements?.get(element.id) ?? element;

const drawBindHighlight = ({ ctx, engineState }) => {
  const target = engineState.elements.find(
    (element) => element.id === engineState.bindTargetId
  );
  if (!target) return;

  const bounds = getElementBounds(
    getEffectiveElement({ element: target, liftedElements: engineState.liftedElements })
  );
  const padding = BIND_HIGHLIGHT_PADDING / engineState.viewport.zoom;
  ctx.strokeStyle = BIND_HIGHLIGHT_COLOR;
  ctx.lineWidth = 2 / engineState.viewport.zoom;
  ctx.strokeRect(
    bounds.x - padding,
    bounds.y - padding,
    bounds.width + padding * 2,
    bounds.height + padding * 2
  );
};

const drawMarquee = ({ ctx, engineState }) => {
  const { marqueeBox, viewport } = engineState;
  if (!marqueeBox) return;

  ctx.fillStyle = MARQUEE_FILL;
  ctx.fillRect(marqueeBox.x, marqueeBox.y, marqueeBox.width, marqueeBox.height);
  ctx.strokeStyle = SELECTION_COLOR;
  ctx.lineWidth = SELECTION_STROKE_WIDTH / viewport.zoom;
  ctx.setLineDash(SELECTION_DASH.map((dash) => dash / viewport.zoom));
  ctx.strokeRect(marqueeBox.x, marqueeBox.y, marqueeBox.width, marqueeBox.height);
  ctx.setLineDash([]);
};

const drawSelectionOutline = ({ ctx, screenBounds }) => {
  ctx.strokeStyle = SELECTION_COLOR;
  ctx.lineWidth = SELECTION_STROKE_WIDTH;
  ctx.setLineDash(SELECTION_DASH);
  ctx.strokeRect(
    screenBounds.x - SELECTION_PADDING,
    screenBounds.y - SELECTION_PADDING,
    screenBounds.width + SELECTION_PADDING * 2,
    screenBounds.height + SELECTION_PADDING * 2
  );
  ctx.setLineDash([]);
};

const drawResizeHandles = ({ ctx, screenBounds }) => {
  ctx.fillStyle = HANDLE_FILL;
  ctx.strokeStyle = SELECTION_COLOR;
  ctx.lineWidth = SELECTION_STROKE_WIDTH;
  getResizeHandleCenters({ screenBounds }).forEach((center) => {
    const half = SELECTION_HANDLE_SIZE / 2;
    ctx.beginPath();
    ctx.rect(center.x - half, center.y - half, SELECTION_HANDLE_SIZE, SELECTION_HANDLE_SIZE);
    ctx.fill();
    ctx.stroke();
  });
};

const drawArrowEndpointHandles = ({ ctx, element, viewport }) => {
  const points = getArrowPoints(element);
  const endpoints = [
    sceneToScreen({ point: { x: points.x1, y: points.y1 }, viewport }),
    sceneToScreen({ point: { x: points.x2, y: points.y2 }, viewport }),
  ];
  ctx.fillStyle = HANDLE_FILL;
  ctx.strokeStyle = SELECTION_COLOR;
  ctx.lineWidth = SELECTION_STROKE_WIDTH;
  endpoints.forEach((endpoint) => {
    ctx.beginPath();
    ctx.arc(endpoint.x, endpoint.y, SELECTION_HANDLE_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  });
};

const drawSelectionUi = ({ ctx, engineState }) => {
  const { editingText, elements, liftedElements, selectedIds, viewport } = engineState;
  if (!selectedIds.size) return;
  const selectedElements = elements
    .filter(
      (element) =>
        selectedIds.has(element.id) && element.id !== editingText?.elementId
    )
    .map((element) => getEffectiveElement({ element, liftedElements }));

  selectedElements.forEach((element) => {
    const screenBounds = getScreenBounds({
      bounds: getElementBounds(element),
      viewport,
    });
    drawSelectionOutline({ ctx, screenBounds });
  });
  if (selectedElements.length !== 1) return;

  const selectedElement = selectedElements[0];
  if (selectedElement.type === CANVAS_ELEMENT_TYPES.ARROW) {
    drawArrowEndpointHandles({ ctx, element: selectedElement, viewport });
  } else if (isBoxElement(selectedElement)) {
    drawResizeHandles({
      ctx,
      screenBounds: getScreenBounds({
        bounds: getElementBounds(selectedElement),
        viewport,
      }),
    });
  }
};

/**
 * Overlay layer: everything that changes during a gesture — the in-progress
 * draft, lifted (moving/resizing) elements, bind-target highlight, marquee,
 * selection outlines and handles. Cheap to repaint at pointer rate.
 * @param {{ canvas: HTMLCanvasElement | null, dpr: number, engineState: object, getImage: Function, palette?: object }} params
 * @returns {void}
 */
export const drawOverlayScene = ({ canvas, dpr, engineState, getImage, palette }) => {
  const ctx = canvas?.getContext("2d");
  if (!ctx) return;

  clearCanvas({ ctx, dpr, size: engineState.size });
  applySceneTransform({ ctx, dpr, viewport: engineState.viewport });
  drawBindHighlight({ ctx, engineState });
  engineState.liftedElements?.forEach((element) =>
    drawCanvasElement({ ctx, element, getImage, palette })
  );
  if (engineState.draftElement) {
    drawCanvasElement({
      ctx,
      element: engineState.draftElement,
      getImage,
      palette,
    });
  }
  drawMarquee({ ctx, engineState });

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawSelectionUi({ ctx, engineState });
};
